import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { theBodyOf } from '../../utils/what-the-api-returns';

/** Somebody the social shows: a name and an avatar, nothing else. */
export interface SocialPerson {
  userId: string;
  displayName: string;
  avatarUrl?: string;
}

/** Where somebody stands with me, which is what the button next to them says. */
export type Relation = 'friend' | 'asked' | 'asks_you' | 'none';

export interface PersonWithRelation extends SocialPerson {
  relation: Relation;
  /** The open request between us, to accept or withdraw it from the search. */
  requestId?: string;
}

export interface Suggestion extends SocialPerson {
  friendsInCommon: number;
}

export interface MyFriends {
  friends: SocialPerson[];
  /** Requests waiting for my answer. */
  received: { id: string; from: SocialPerson }[];
  /** Requests I sent and nobody answered yet. */
  sent: { id: string; to: SocialPerson }[];
}

/** One outing in the feed: who, which sport, which day. Nothing else, on purpose (Maude, 21/10/2026). */
export interface FeedPost {
  id: string;
  author: SocialPerson;
  sportId?: string;
  outingAt: string;
  mine: boolean;
}

export interface FeedPage {
  posts: FeedPost[];
  /** What to pass as `after` for the next page, absent at the end. */
  next?: string;
}

export interface MyReferral {
  code: string;
  rewardedThisYear: number;
  rewardsLeftThisYear: number;
}

export type Surprise = 'badge_category_topped' | 'ten_outings_with_friends' | 'one_year_on_smatchy';

export interface EarnedSurprise {
  surprise: Surprise;
  earnedAt: string;
  seenAt?: string;
}

/** A settled referral the referrer has still to see. `over_the_year`: the five bonuses of the year were already received. */
export interface MyReferralReward {
  inviteeId: string;
  firstName: string;
  outcome: 'rewarded' | 'over_the_year';
  settledAt: string;
}

/** My own settings for the friends (Maude, 29 October 2026). */
export interface MyFriendsSettings {
  receivesFriendRequests: boolean;
}

export type SocialRefusal =
  | 'cannot_ask_this_person'
  | 'already_friends'
  | 'already_asked'
  | 'too_many_today'
  | 'unknown_code'
  | 'own_code'
  | 'already_claimed';

/**
 * The social of the gamification (lot C): friends, their feed, the referral,
 * the surprises.
 *
 * Called directly rather than through the generated client, like the streak
 * and the duo: these routes are not in the generated SDK. They are new
 * (October 2026), so they answer the object itself, but every read still
 * goes through `theBodyOf`, which costs nothing and survives a route that
 * one day comes back wrapped.
 *
 * Every route answers 404 `module_off` while the switch `social` is off. The
 * screens are not reached then (the routes carry the guard), and the calls
 * made from elsewhere (Explorer, the opening, the onboarding) ask the switch
 * first rather than read a 404 as « nothing to show ».
 */
@Injectable({ providedIn: 'root' })
export class MySocialService {
  private readonly http = inject(HttpClient);

  friends(): Observable<MyFriends> {
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/friends`)
      .pipe(map((response) => theBodyOf<MyFriends>(response) ?? { friends: [], received: [], sent: [] }));
  }

  /** Asks somebody. Asking somebody who already asked me accepts their request, on the API's side. */
  ask(userId: string): Observable<void> {
    return this.http
      .post<unknown>(`${environment.api}/api/v1/social/friends/requests`, { userId })
      .pipe(map(() => undefined));
  }

  accept(requestId: string): Observable<void> {
    return this.http
      .post<unknown>(`${environment.api}/api/v1/social/friends/requests/${requestId}/accept`, {})
      .pipe(map(() => undefined));
  }

  /** Declines a request I received, or withdraws one I sent: the same gesture. */
  dismiss(requestId: string): Observable<void> {
    return this.http
      .delete<unknown>(`${environment.api}/api/v1/social/friends/requests/${requestId}`)
      .pipe(map(() => undefined));
  }

  /** Nobody is told (Maude, 21/10/2026). */
  unfriend(userId: string): Observable<void> {
    return this.http.delete<unknown>(`${environment.api}/api/v1/social/friends/${userId}`).pipe(map(() => undefined));
  }

  /**
   * Absent reads as open, never as closed: an answer without the field comes
   * from an API that never closed anybody. A failed read stays an error, so
   * the screen does not show « on » for somebody who closed it.
   */
  settings(): Observable<MyFriendsSettings> {
    return this.http.get<unknown>(`${environment.api}/api/v1/social/settings`).pipe(
      map((response) => ({
        receivesFriendRequests: theBodyOf<Partial<MyFriendsSettings>>(response)?.receivesFriendRequests !== false,
      })),
    );
  }

  setReceivesFriendRequests(receivesFriendRequests: boolean): Observable<void> {
    return this.http
      .put<unknown>(`${environment.api}/api/v1/social/settings`, { receivesFriendRequests })
      .pipe(map(() => undefined));
  }

  search(query: string): Observable<PersonWithRelation[]> {
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/people`, { params: new HttpParams().set('q', query) })
      .pipe(map((response) => theBodyOf<PersonWithRelation[]>(response) ?? []));
  }

  suggestions(): Observable<Suggestion[]> {
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/suggestions`)
      .pipe(map((response) => theBodyOf<Suggestion[]>(response) ?? []));
  }

  feed(after?: string): Observable<FeedPage> {
    const params = after ? new HttpParams().set('after', after) : undefined;
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/feed`, { params })
      .pipe(map((response) => theBodyOf<FeedPage>(response) ?? { posts: [] }));
  }

  referral(): Observable<MyReferral> {
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/referral`)
      .pipe(map((response) => theBodyOf<MyReferral>(response) as MyReferral));
  }

  /**
   * Whose code this is. `undefined` when nobody has it (a 404), so that the
   * onboarding can say so; a name absent from a known code means a block
   * stands between the two, and the field then says nothing either way.
   */
  codeOwner(code: string): Observable<{ displayName?: string }> {
    const safe = encodeURIComponent(code);
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/referral/codes/${safe}`)
      .pipe(map((response) => theBodyOf<{ displayName?: string }>(response) ?? {}));
  }

  claim(code: string): Observable<void> {
    return this.http
      .post<unknown>(`${environment.api}/api/v1/social/referral/claim`, { code })
      .pipe(map(() => undefined));
  }

  referralRewards(): Observable<MyReferralReward[]> {
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/referral/rewards`)
      .pipe(map((response) => theBodyOf<MyReferralReward[]>(response) ?? []));
  }

  referralRewardSeen(inviteeId: string): Observable<void> {
    const safe = encodeURIComponent(inviteeId);
    return this.http
      .post<unknown>(`${environment.api}/api/v1/social/referral/rewards/${safe}/seen`, {})
      .pipe(map(() => undefined));
  }

  surprises(): Observable<EarnedSurprise[]> {
    return this.http
      .get<unknown>(`${environment.api}/api/v1/social/surprises`)
      .pipe(map((response) => theBodyOf<EarnedSurprise[]>(response) ?? []));
  }

  surpriseSeen(surprise: Surprise): Observable<void> {
    return this.http
      .post<unknown>(`${environment.api}/api/v1/social/surprises/${surprise}/seen`, {})
      .pipe(map(() => undefined));
  }
}

/** The reason a 409 carries, if it carries one. */
export const socialRefusalOf = (error: unknown): SocialRefusal | undefined => {
  const body = (error as { status?: number; error?: { reason?: SocialRefusal } } | null) ?? {};
  return body.status === 409 ? body.error?.reason : undefined;
};
